import { Color, Spacing } from '@persona-id/design-tokens';
import _ from 'lodash';
import styled from 'styled-components';

import AttributeItem from 'dashboard/components/AttributeItem';
import GraphHoverCardSelfieImage from 'dashboard/components/GraphHoverCardSelfieImage';
import { UppercaseLabel } from 'dashboard/components/Typography';
import {
  DatumTypeToken,
  GraphProperty,
  NodeRenderConfig,
  PersonaPropertyValues,
  PropertySource,
  Tag,
} from 'dashboard/models/graph';
import {
  HydratedAccount,
  ServerHydratedAccount,
  ServerHydratedProperty,
} from 'dashboard/models/graph-hydration';
import Attribute from 'design-system/components/Attribute';
import Sticker, { LabelBackgrounds } from 'design-system/components/Sticker';
import { BASE } from 'design-system/variables/spacing';
import {
  ACCOUNT_DATUM_TYPE,
  ACTIVE_ACCOUNT_STATUS,
  DATUM_TYPE_DISPLAY_NAMES,
} from 'lib/constants/graph';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

const StickerContainer = styled('div')`
  padding-right: ${Spacing[4]};
  padding-bottom: ${Spacing[2]};
`;

const SectionTitle = styled(UppercaseLabel)`
  color: ${Color.CoreSlate900};
`;

const AttributesColumn = styled('div')`
  display: grid;
  grid-row-gap: ${Spacing[4]};
`;

interface GraphHoverCardAccountProps {
  hydratedAccount: HydratedAccount;
  cardMenu?: boolean;
  getNodeRenderConfig: (token: DatumTypeToken) => NodeRenderConfig;
}

interface InquiryBreakdownItem {
  variant: LabelBackgrounds;
  inquiryStatus: keyof ServerHydratedAccount['inquiry-counts'];
  displayLabel: string;
}

const INQUIRY_BREAKDOWN_ITEMS: InquiryBreakdownItem[] = [
  {
    variant: 'green',
    inquiryStatus: 'approved',
    displayLabel: 'Approved',
  },
  {
    variant: 'pink',
    inquiryStatus: 'declined',
    displayLabel: 'Declined',
  },
  {
    variant: 'yellow',
    inquiryStatus: 'needs-review',
    displayLabel: 'Needs review',
  },
  {
    variant: 'green',
    inquiryStatus: 'completed',
    displayLabel: 'Completed',
  },
  {
    variant: 'pink',
    inquiryStatus: 'failed',
    displayLabel: 'Failed',
  },
  {
    variant: 'yellow',
    inquiryStatus: 'expired',
    displayLabel: 'Expired',
  },
  {
    variant: 'grey',
    inquiryStatus: 'created',
    displayLabel: 'Created',
  },
  {
    variant: 'grey',
    inquiryStatus: 'pending',
    displayLabel: 'Pending',
  },
];

const GraphHoverCardAccount = ({
  hydratedAccount,
  cardMenu = false,
  getNodeRenderConfig,
}: GraphHoverCardAccountProps) => {
  const titleSpacing = cardMenu ? 0 : BASE;
  const showAccountStatus = useFeatureFlag('dashboard:accounts-show-account-status');
  const contextMenu = getNodeRenderConfig(ACCOUNT_DATUM_TYPE.token).contextMenu;
  const serverHydratedAccount = hydratedAccount.hydrationData;

  // Gracefully render nothing
  if (!serverHydratedAccount) {
    return null;
  }

  const name = [
    serverHydratedAccount.name.first,
    serverHydratedAccount.name.middle,
    serverHydratedAccount.name.last,
  ]
    .filter((nameFragment) => nameFragment != null)
    .join(' ');

  const renderGraphProperty = (graphProperty: GraphProperty, key: number) => {
    switch (graphProperty.source) {
      case PropertySource.Persona: {
        switch (graphProperty.value) {
          case PersonaPropertyValues.Tags: {
            return (
              <AttributeItem
                title='Tags'
                value={
                  hydratedAccount.hydrationData.tags.length
                    ? hydratedAccount.hydrationData.tags.map((tag: Tag) => (
                        <StickerContainer key={tag}>
                          <Sticker data-test={`graph-account-hover-card__tags--${tag}`}>
                            {tag}
                          </Sticker>
                        </StickerContainer>
                      ))
                    : null
                }
                isRedacted={false}
                wordBreak='break-all'
                placeholder='-'
                dataTest='graph-account-hover-card__tags'
                titleSpacing={titleSpacing}
                key={key}
              />
            );
          }
          case PersonaPropertyValues.Name: {
            return (
              <AttributeItem
                title='Name'
                value={name}
                isRedacted={false}
                wordBreak='break-all'
                placeholder='-'
                dataTest='graph-account-hover-card__name'
                titleSpacing={titleSpacing}
                key={key}
              />
            );
          }
          case PersonaPropertyValues.CreatedAt: {
            return (
              <AttributeItem
                title='Created At'
                value={new Date(serverHydratedAccount['created-at']).toLocaleString()}
                isRedacted={false}
                wordBreak='break-all'
                placeholder='-'
                dataTest='graph-account-hover-card__created_at'
                titleSpacing={titleSpacing}
                key={key}
              />
            );
          }
          case PersonaPropertyValues.Status: {
            if (serverHydratedAccount.status && showAccountStatus) {
              return (
                <AttributeItem
                  title='Status'
                  value={
                    <Sticker
                      variant={
                        serverHydratedAccount.status === ACTIVE_ACCOUNT_STATUS ? 'success' : 'basic'
                      }
                    >
                      {serverHydratedAccount.status}
                    </Sticker>
                  }
                  isRedacted={false}
                  wordBreak='break-all'
                  placeholder='-'
                  dataTest='graph-account-hover-card__status'
                  titleSpacing={titleSpacing}
                  key={key}
                />
              );
            }
            break;
          }
          case PersonaPropertyValues.ReferenceId: {
            return (
              <AttributeItem
                title='Reference ID'
                value={serverHydratedAccount['reference-id']}
                isRedacted={false}
                wordBreak='break-all'
                placeholder='-'
                dataTest='graph-account-hover-card__reference_id'
                titleSpacing={titleSpacing}
                key={key}
              />
            );
          }
          case PersonaPropertyValues.NumberOfInquiries: {
            return (
              <AttributeItem
                title='Number of Inquiries Declined'
                value={serverHydratedAccount['inquiry-counts']['declined']}
                isRedacted={false}
                wordBreak='break-all'
                placeholder='-'
                dataTest='graph-account-hover-card__number_of_inquiries'
                titleSpacing={titleSpacing}
                key={key}
              />
            );
          }
          case PersonaPropertyValues.InquiryBreakdown: {
            return (
              <Attribute
                label='Inquiry Breakdown'
                value={INQUIRY_BREAKDOWN_ITEMS.map((item) => {
                  const count = serverHydratedAccount['inquiry-counts'][item.inquiryStatus];
                  if (count > 0) {
                    return (
                      <>
                        <Sticker variant={item.variant}>
                          {item.displayLabel} {count}
                        </Sticker>{' '}
                      </>
                    );
                  } else {
                    return null;
                  }
                })}
              />
            );
          }
        }
        break;
      }
      case PropertySource.Account: {
        const property = serverHydratedAccount.properties.find(
          (hydratedProperty: ServerHydratedProperty) => {
            return _.isEqual(hydratedProperty['property-key'], graphProperty);
          },
        );
        if (property) {
          return (
            <AttributeItem
              title={property['display-name']}
              value={property.values}
              isRedacted={false}
              wordBreak='break-all'
              placeholder='-'
              titleSpacing={titleSpacing}
              key={key}
            />
          );
        }
        break;
      }
    }
  };

  return (
    <>
      <SectionTitle>{DATUM_TYPE_DISPLAY_NAMES[ACCOUNT_DATUM_TYPE.token]}</SectionTitle>
      <AttributesColumn>
        {Object.prototype.hasOwnProperty.call(serverHydratedAccount, 'selfies') &&
          serverHydratedAccount['selfies'].length > 0 && (
            <GraphHoverCardSelfieImage selfieFile={serverHydratedAccount['selfies'][0]} />
          )}
        <AttributeItem
          title='ID'
          value={serverHydratedAccount.token}
          isRedacted={false}
          wordBreak='break-all'
          placeholder='-'
          dataTest='graph-account-hover-card__id'
          titleSpacing={titleSpacing}
        />
        {contextMenu.map((graphProperty, index) => renderGraphProperty(graphProperty, index))}
      </AttributesColumn>
    </>
  );
};

export default GraphHoverCardAccount;
